import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import debounce from "lodash/debounce"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import CustomCheckbox from "../../components/OnboardingComponents/CustomCheckbox"
import CustomIconButton from "../../components/OnboardingComponents/CustomIconButton"
import CustomInput from "../../components/OnboardingComponents/CustomInput"
import CategoriesMultiselect from "../../components/OnboardingComponents/CategoriesMultiselect"
import EmptyState from "../../components/Common/EmptyState"
import MediaFallbackImage from "../../components/Common/MediaFallbackImage"
import { ICONS } from "../../constants/icons"
import { formatDate } from "../../helpers/formatDate"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { Avatar } from "../../components/DashboardComponents/AvatarGroup"
import { REVIEW_INTEREST_OPTIONS } from "./cityHubData"
import { get, patch, post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import useInfinitePagination from "../../hooks/useInfinitePagination"
import InfiniteListSentinel from "../../components/Listing/InfiniteListSentinel"
import {
  getCityTierRequiredLevel,
  isOwnedCityHubAccessible,
  mapApiCityToCityHubCity,
  resolveCityHubCityForLevel,
} from "./cityHubCity"
import { unwrapCityHubPagedResult } from "./cityHubTabs"
import useCategories from "../../hooks/useCategories"
import {
  PHOTO_REQUEST_MESSAGE_MAX,
  PHOTO_REQUEST_PORTFOLIO_MAX,
  PHOTO_REQUEST_SUBJECT_MAX,
  mapPhotoRequestFormValues,
  toPhotoRequestPayload,
  unwrapPhotoRequestResponse,
  isPendingPhotoRequest,
} from "./cityPhotographyRequest"
import {
  REVIEW_REQUEST_FIRST_MAX,
  REVIEW_REQUEST_WHY_MAX,
  mapReviewWriterRequestFormValues,
  toReviewWriterRequestPayload,
  unwrapReviewWriterRequestResponse,
  isPendingReviewWriterRequest,
} from "./cityReviewWriterRequest"
import {
  REVIEW_DESCRIPTION_MAX,
  REVIEW_MAX_MEDIA_BYTES,
  REVIEW_MEDIA_ACCEPT,
  REVIEW_TITLE_MAX,
  buildCityReviewFormData,
  fetchCityReviewDetail,
  getEmptyReviewForm,
  isAcceptedReviewFile,
  mapFileToReviewPreview,
  mapReviewToForm,
  revokeReviewPreviews,
} from "./cityReviews"
import {
  MAX_PHOTO_BYTES,
  MAX_PHOTO_FILES,
  PHOTO_ACCEPT,
  PHOTO_DESCRIPTION_MAX,
  PHOTO_TITLE_MAX,
  buildPhotographyFormData,
  fetchPhotographyDetail,
  getEmptyPhotographyForm,
  isAcceptedPhotoFile,
  isVideoMime,
  mapFileToPhotoPreview,
  mapPhotographyDetailMedias,
  mapPhotographyDetailTags,
  mapPhotographyToForm,
  revokePhotoPreview,
  revokePhotoPreviews,
} from "./cityPhotography"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const photographySchema = yup.object().shape({
  title: yup
    .string()
    .trim()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(PHOTO_TITLE_MAX, `Title must be ${PHOTO_TITLE_MAX} characters or less`),
  description: yup
    .string()
    .trim()
    .required("Description is required")
    .min(10, "Please provide at least 10 characters")
    .max(
      PHOTO_DESCRIPTION_MAX,
      `Description must be ${PHOTO_DESCRIPTION_MAX} characters or less`,
    ),
  tags: yup
    .array()
    .of(yup.string())
    .min(1, "Select at least one tag")
    .required("Select at least one tag"),
  previews: yup
    .array()
    .min(1, "Add at least one photo or video")
    .required("Add at least one photo or video"),
})

const reviewSchema = yup.object().shape({
  title: yup
    .string()
    .trim()
    .required("Review title is required")
    .max(REVIEW_TITLE_MAX, `Title must be ${REVIEW_TITLE_MAX} characters or less`),
  description: yup
    .string()
    .trim()
    .required("Your review is required")
    .max(
      REVIEW_DESCRIPTION_MAX,
      `Review must be ${REVIEW_DESCRIPTION_MAX} characters or less`,
    ),
  previews: yup
    .array()
    .min(1, "Please upload a cover image")
    .required("Please upload a cover image"),
})

const reviewWriterRequestSchema = yup.object().shape({
  what_to_review_first: yup
    .string()
    .trim()
    .required("Tell us what you'd like to review first")
    .max(
      REVIEW_REQUEST_FIRST_MAX,
      `Must be ${REVIEW_REQUEST_FIRST_MAX} characters or less`,
    ),
  why_write_reviews: yup
    .string()
    .trim()
    .required("Tell us why you want to write reviews")
    .max(
      REVIEW_REQUEST_WHY_MAX,
      `Must be ${REVIEW_REQUEST_WHY_MAX} characters or less`,
    ),
  areas_of_interest: yup.array().of(yup.string()),
})

const AddReviewModal = ({
  isOpen,
  onClose,
  cityId,
  reviewId = null,
  onSuccess,
}) => {
  const fileInputRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const isEdit = Boolean(reviewId)
  const [initialValues, setInitialValues] = useState(getEmptyReviewForm)
  const [loadedReviewId, setLoadedReviewId] = useState(null)
  const [formKey, setFormKey] = useState(0)
  const isEditLoading = isEdit && loadedReviewId !== reviewId
  const isModalVisible = isOpen && !isEditLoading

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!isOpen) {
      setInitialValues(getEmptyReviewForm())
      setLoadedReviewId(null)
      return undefined
    }

    if (!reviewId) {
      setInitialValues({
        ...getEmptyReviewForm(),
        city_id: cityId || "",
      })
      setLoadedReviewId(null)
      setFormKey(key => key + 1)
      return undefined
    }

    let cancelled = false
    setLoadedReviewId(null)

    ;(async () => {
      const result = await fetchCityReviewDetail(cityId, reviewId)
      if (cancelled) return

      if (!result?.ok || !result.item) {
        toastApiError(result?.response, "Failed to load review")
        onCloseRef.current?.()
        return
      }

      setInitialValues(mapReviewToForm(result.item))
      setLoadedReviewId(reviewId)
      // Remount Formik once with loaded data — avoid enableReinitialize mid-edit.
      setFormKey(key => key + 1)
    })()

    return () => {
      cancelled = true
    }
  }, [isOpen, reviewId, cityId])

  const handleSubmit = async (values, { setSubmitting }) => {
    const resolvedCityId = values.city_id || cityId
    if (!resolvedCityId && !isEdit) {
      setSubmitting(false)
      return
    }

    try {
      const formData = buildCityReviewFormData({
        values: { ...values, city_id: resolvedCityId },
        cityId: resolvedCityId,
        isEdit,
      })
      const response = isEdit
        ? await patch(endpoints.updateReview(reviewId), formData, {
            skipErrorToast: true,
          })
        : await post(endpoints.createReview, formData, {
            skipErrorToast: true,
          })

      if (!response?.data?.success) {
        toastApiError(
          response,
          isEdit ? "Failed to update review" : "Failed to submit review",
        )
        return
      }

      toast.success(
        response.data.message ||
          (isEdit
            ? "Review updated successfully"
            : "Review submitted successfully"),
        TOAST_OPTIONS,
      )
      revokeReviewPreviews(values.previews)
      onSuccess?.()
      onCloseRef.current?.()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      key={
        isOpen ? `review-form-${reviewId || "create"}-${formKey}` : "closed"
      }
      initialValues={initialValues}
      enableReinitialize={false}
      validationSchema={reviewSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        setFieldTouched,
        setFieldError,
        isSubmitting,
      }) => {
        const artworkPreview = values.previews[0] || null

        const closeModal = () => {
          if (isSubmitting) return
          revokeReviewPreviews(values.previews)
          onCloseRef.current?.()
        }

        const acceptArtwork = fileList => {
          setFieldTouched("previews", true, false)
          const file = Array.from(fileList || []).find(item =>
            isAcceptedReviewFile(item),
          )
          if (!file) {
            setFieldError("previews", "Please upload a PNG, JPG, or WEBP image")
            return
          }
          if (file.size > REVIEW_MAX_MEDIA_BYTES) {
            setFieldError("previews", "Image must be 5MB or smaller")
            return
          }

          revokeReviewPreviews(values.previews)
          const nextRemoved = [...(values.removedMediaIds || [])]
          values.previews.forEach(item => {
            if (item?.isExisting && item?.mediaId) {
              nextRemoved.push(item.mediaId)
            }
          })
          setFieldValue("removedMediaIds", [...new Set(nextRemoved)])
          setFieldValue("previews", [mapFileToReviewPreview(file)])
          setFieldError("previews", undefined)
        }

        const clearArtwork = () => {
          const nextRemoved = [...(values.removedMediaIds || [])]
          values.previews.forEach(item => {
            if (item?.isExisting && item?.mediaId) {
              nextRemoved.push(item.mediaId)
            }
          })
          revokeReviewPreviews(values.previews)
          setFieldValue("removedMediaIds", [...new Set(nextRemoved)])
          setFieldValue("previews", [])
          setFieldTouched("previews", true, false)
        }

        return (
          <Modal
            isOpen={isModalVisible}
            toggle={isSubmitting ? undefined : closeModal}
            centered
            backdrop={isSubmitting ? "static" : true}
            keyboard={!isSubmitting}
            className="amok-modal amok-modal--city-review"
            contentClassName="amok-modal__content"
            backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
          >
            <ModalBody className="amok-modal__body city-hub-review-modal__body">
              <Form className="city-hub-review-modal" noValidate>
                <div className="city-hub-review-modal__main">
                  <div className="city-hub-review-modal__header">
                    <h3 className="city-hub-review-modal__title">
                      {isEdit ? "Edit Review" : "Add a Review"}
                    </h3>
                    <p className="city-hub-review-modal__help">
                      {isEdit
                        ? "Update the review details and cover image."
                        : "Your review will be sent to the admin for approval before being published."}
                    </p>
                  </div>

                  <div className="city-hub-review-modal__fields">
                    <CustomInput
                        label="Review Title"
                        name="title"
                        required
                        maxLength={REVIEW_TITLE_MAX}
                        placeholder="e.g: Neon Drift at Deep Ellum"
                        value={values.title}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("title", true)}
                        error={touched.title && errors.title}
                        helperText={`${values.title.length}/${REVIEW_TITLE_MAX}`}
                      />

                      <CustomInput
                        label="Your Review"
                        name="description"
                        textarea
                        rows={4}
                        required
                        maxLength={REVIEW_DESCRIPTION_MAX}
                        placeholder="Write your full review — share your experience with the show, album, film, or exhibit, what stood out, and any recommendations..."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("description", true)}
                        error={touched.description && errors.description}
                        helperText={`${values.description.length}/${REVIEW_DESCRIPTION_MAX}`}
                      />

                      <input
                        ref={fileInputRef}
                        type="file"
                        id="city-hub-review-artwork-upload"
                        className="city-hub-review-modal__upload-input"
                        accept={REVIEW_MEDIA_ACCEPT}
                        onChange={e => {
                          acceptArtwork(e.target.files)
                          e.target.value = ""
                        }}
                      />

                      {artworkPreview?.url ? (
                        <div className="city-hub-review-modal__artwork-preview">
                          <img
                            src={artworkPreview.url}
                            alt={artworkPreview.name || "Album artwork"}
                          />
                          <div className="city-hub-review-modal__artwork-actions">
                            <CustomButton
                              variant="secondary"
                              size="sm"
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              Replace
                            </CustomButton>
                            <CustomButton
                              variant="danger"
                              size="sm"
                              type="button"
                              onClick={clearArtwork}
                            >
                              Remove
                            </CustomButton>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="city-hub-review-modal__upload"
                          role="button"
                          tabIndex={0}
                          onClick={() => fileInputRef.current?.click()}
                          onKeyDown={event => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault()
                              fileInputRef.current?.click()
                            }
                          }}
                          onDragOver={event => {
                            event.preventDefault()
                            event.stopPropagation()
                          }}
                          onDrop={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            acceptArtwork(event.dataTransfer?.files)
                          }}
                        >
                          <span
                            className="city-hub-review-modal__upload-icon"
                            aria-hidden="true"
                          >
                            <img
                              src={ICONS.callUploadExport}
                              alt=""
                              width={16}
                              height={16}
                            />
                          </span>
                          <p className="city-hub-review-modal__upload-title">
                            Upload album artwork or cover image
                          </p>
                          <p className="city-hub-review-modal__upload-hint">
                            PNG, JPG, WEBP (max 5MB)
                          </p>
                        </div>
                      )}
                      {touched.previews && errors.previews ? (
                        <p className="amok-field__message amok-field__message--error">
                          {errors.previews}
                        </p>
                      ) : null}
                  </div>
                </div>

                <div className="city-hub-review-modal__actions">
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="city-hub-review-modal__cancel"
                    onClick={closeModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="city-hub-review-modal__submit"
                    loading={isSubmitting}
                  >
                    {isEdit ? "Update Review" : "Submit Review"}
                  </CustomButton>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        )
      }}
    </Formik>
  )
}

const BecomeReviewWriterModal = ({
  isOpen,
  onClose,
  cityId,
  request = null,
  onSuccess,
}) => {
  const isEdit = isPendingReviewWriterRequest(request)
  const initialValues = mapReviewWriterRequestFormValues(request)

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!cityId && !isEdit) {
      setSubmitting(false)
      return
    }

    try {
      const payload = toReviewWriterRequestPayload(values, {
        cityId,
        includeCityId: !isEdit,
      })
      const response = isEdit
        ? await patch(endpoints.updateReviewWriterRequest(request.id), payload)
        : await post(endpoints.createReviewWriterRequest, payload)

      if (!response?.data?.success) {
        toastApiError(
          response,
          isEdit
            ? "Failed to update review writer request"
            : "Failed to submit review writer request",
        )
        return
      }

      toast.success(
        response.data.message ||
          (isEdit
            ? "Request updated successfully"
            : "Request submitted successfully"),
        TOAST_OPTIONS,
      )
      onSuccess?.(unwrapReviewWriterRequestResponse(response) || request || null)
      onClose?.()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      key={isOpen ? request?.id || "create" : "closed"}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={reviewWriterRequestSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => {
        const toggleInterest = value => {
          const current = Array.isArray(values.areas_of_interest)
            ? values.areas_of_interest
            : []
          const next = current.includes(value)
            ? current.filter(item => item !== value)
            : [...current, value]
          setFieldValue("areas_of_interest", next)
          setFieldTouched("areas_of_interest", true)
        }

        return (
          <Modal
            isOpen={isOpen}
            toggle={isSubmitting ? undefined : onClose}
            centered
            backdrop={isSubmitting ? "static" : true}
            keyboard={!isSubmitting}
            className="amok-modal amok-modal--city-review"
            contentClassName="amok-modal__content"
            backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
          >
            <ModalBody className="amok-modal__body city-hub-review-modal__body">
              <Form className="city-hub-review-modal" noValidate>
                <div className="city-hub-review-modal__main">
                  <div className="city-hub-review-modal__header">
                    <h3 className="city-hub-review-modal__title">
                      {isEdit ? "Edit Request" : "Become a Review Writer"}
                    </h3>
                    <p className="city-hub-review-modal__help">
                      {isEdit
                        ? "Update your request. It stays with the city editor until they accept it."
                        : "Want to write about local shows, albums, and indie films? Apply to join our team of review writers."}
                    </p>
                  </div>

                  <div className="city-hub-review-modal__fields">
                    <CustomInput
                      label="What would you like to review first?"
                      name="what_to_review_first"
                      required
                      maxLength={REVIEW_REQUEST_FIRST_MAX}
                      placeholder="e.g: A recent show, album, or film you'd want to cover"
                      value={values.what_to_review_first}
                      onChange={handleChange}
                      onBlur={() =>
                        setFieldTouched("what_to_review_first", true)
                      }
                      error={
                        touched.what_to_review_first &&
                        errors.what_to_review_first
                      }
                      helperText={`${values.what_to_review_first.length}/${REVIEW_REQUEST_FIRST_MAX}`}
                    />

                    <CustomInput
                      label="Why do you want to write reviews?"
                      name="why_write_reviews"
                      textarea
                      rows={3}
                      required
                      maxLength={REVIEW_REQUEST_WHY_MAX}
                      placeholder="Brief motivation — what draws you to local culture?"
                      value={values.why_write_reviews}
                      onChange={handleChange}
                      onBlur={() => setFieldTouched("why_write_reviews", true)}
                      error={
                        touched.why_write_reviews && errors.why_write_reviews
                      }
                      helperText={`${values.why_write_reviews.length}/${REVIEW_REQUEST_WHY_MAX}`}
                    />

                    <div className="city-hub-review-modal__interests-field">
                      <span className="city-hub-review-modal__interests-label">
                        Areas of Interest
                      </span>
                      <div className="city-hub-review-modal__interests">
                        {REVIEW_INTEREST_OPTIONS.map(option => {
                          const checked = (
                            values.areas_of_interest || []
                          ).includes(option.value)
                          return (
                            <CustomCheckbox
                              key={option.value}
                              className={`city-hub-review-modal__interest ${
                                checked
                                  ? "city-hub-review-modal__interest--selected"
                                  : ""
                              }`}
                              checked={checked}
                              onChange={() => toggleInterest(option.value)}
                              label={option.label}
                              checkIconSrc={ICONS.check}
                            />
                          )
                        })}
                      </div>
                    </div>

                    <div className="city-hub-review-modal__note" role="note">
                      <span
                        className="city-hub-review-modal__note-icon"
                        aria-hidden="true"
                      >
                        <img
                          src={ICONS.callInfoCircle}
                          alt=""
                          width={24}
                          height={24}
                        />
                      </span>
                      <p className="city-hub-review-modal__note-text">
                        {request?.action_reason
                          ? request.action_reason
                          : "Your request will be reviewed by the city editor. Once approved, you\u2019ll be able to publish reviews."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="city-hub-review-modal__actions">
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="city-hub-review-modal__cancel"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="city-hub-review-modal__submit"
                    loading={isSubmitting}
                    disabled={
                      !String(values.what_to_review_first || "").trim() ||
                      !String(values.why_write_reviews || "").trim()
                    }
                  >
                    {isEdit ? "Update Request" : "Apply to Write"}
                  </CustomButton>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        )
      }}
    </Formik>
  )
}

const contributorRequestSchema = yup.object().shape({
  subject: yup
    .string()
    .trim()
    .required("Subject is required")
    .max(
      PHOTO_REQUEST_SUBJECT_MAX,
      `Subject must be ${PHOTO_REQUEST_SUBJECT_MAX} characters or less`,
    ),
  message: yup
    .string()
    .trim()
    .required("Message is required")
    .max(
      PHOTO_REQUEST_MESSAGE_MAX,
      `Message must be ${PHOTO_REQUEST_MESSAGE_MAX} characters or less`,
    ),
  portfolio_link: yup
    .string()
    .trim()
    .max(
      PHOTO_REQUEST_PORTFOLIO_MAX,
      `Portfolio link must be ${PHOTO_REQUEST_PORTFOLIO_MAX} characters or less`,
    ),
})

const BecomeContributorModal = ({
  isOpen,
  onClose,
  cityId,
  request = null,
  onSuccess,
}) => {
  const isEdit = isPendingPhotoRequest(request)
  const initialValues = mapPhotoRequestFormValues(request)

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!cityId && !isEdit) {
      setSubmitting(false)
      return
    }

    try {
      const payload = toPhotoRequestPayload(values, {
        cityId,
        includeCityId: !isEdit,
      })
      const response = isEdit
        ? await patch(
            endpoints.updateCityPhotographyRequest(request.id),
            payload,
          )
        : await post(endpoints.createCityPhotographyRequest, payload)

      if (!response?.data?.success) {
        toastApiError(
          response,
          isEdit
            ? "Failed to update contribution request"
            : "Failed to submit contribution request",
        )
        return
      }

      toast.success(
        response.data.message ||
          (isEdit
            ? "Request updated successfully"
            : "Request submitted successfully"),
        TOAST_OPTIONS,
      )
      onSuccess?.(unwrapPhotoRequestResponse(response) || request || null)
      onClose?.()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      key={isOpen ? request?.id || "create" : "closed"}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={contributorRequestSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldTouched,
        isSubmitting,
      }) => (
        <Modal
          isOpen={isOpen}
          toggle={isSubmitting ? undefined : onClose}
          centered
          backdrop={isSubmitting ? "static" : true}
          keyboard={!isSubmitting}
          className="amok-modal amok-modal--city-review"
          contentClassName="amok-modal__content"
          backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
        >
          <ModalBody className="amok-modal__body city-hub-review-modal__body">
            <Form className="city-hub-review-modal" noValidate>
              <div className="city-hub-review-modal__main">
                <div className="city-hub-review-modal__header">
                  <h3 className="city-hub-review-modal__title">
                    {isEdit ? "Edit Request" : "Become a Contributor"}
                  </h3>
                  <p className="city-hub-review-modal__help">
                    {isEdit
                      ? "Update your request. It stays with the city editor until they accept it."
                      : "Interested in photographing local concerts? Introduce yourself to the city editor to get started."}
                  </p>
                </div>

                <div className="city-hub-review-modal__fields">
                  <CustomInput
                    label="Subject"
                    name="subject"
                    required
                    maxLength={PHOTO_REQUEST_SUBJECT_MAX}
                    placeholder="Request for city photography"
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("subject", true)}
                    error={touched.subject && errors.subject}
                    helperText={`${values.subject.length}/${PHOTO_REQUEST_SUBJECT_MAX}`}
                  />

                  <CustomInput
                    label="Portfolio Link (optional)"
                    name="portfolio_link"
                    maxLength={PHOTO_REQUEST_PORTFOLIO_MAX}
                    placeholder="https://yoursite.com or @instagram_handle"
                    value={values.portfolio_link}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("portfolio_link", true)}
                    error={touched.portfolio_link && errors.portfolio_link}
                    helperText={`${values.portfolio_link.length}/${PHOTO_REQUEST_PORTFOLIO_MAX}`}
                  />

                  <CustomInput
                    label="Message to Editor"
                    name="message"
                    textarea
                    rows={4}
                    required
                    maxLength={PHOTO_REQUEST_MESSAGE_MAX}
                    placeholder="Tell us about your photography experience and interest..."
                    value={values.message}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("message", true)}
                    error={touched.message && errors.message}
                    helperText={`${values.message.length}/${PHOTO_REQUEST_MESSAGE_MAX}`}
                  />

                  <div className="city-hub-review-modal__note" role="note">
                    <span
                      className="city-hub-review-modal__note-icon"
                      aria-hidden="true"
                    >
                      <img
                        src={ICONS.callInfoCircle}
                        alt=""
                        width={24}
                        height={24}
                      />
                    </span>
                    <p className="city-hub-review-modal__note-text">
                      {request?.action_reason
                        ? request.action_reason
                        : "Your request will be reviewed by the local city editor. You\u2019ll be notified once approved."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="city-hub-review-modal__actions">
                <CustomButton
                  variant="danger"
                  size="md"
                  type="button"
                  className="city-hub-review-modal__cancel"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  className="city-hub-review-modal__submit"
                  loading={isSubmitting}
                  disabled={
                    !String(values.subject || "").trim() ||
                    !String(values.message || "").trim()
                  }
                >
                  {isEdit ? "Update Request" : "Submit Request"}
                </CustomButton>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      )}
    </Formik>
  )
}

const acceptPhotoFiles = (
  fileList,
  values,
  setValues,
  { replacePreviewId = null } = {},
) => {
  const files = Array.from(fileList || [])
  if (files.length === 0) return

  const hasInvalidType = files.some(file => !isAcceptedPhotoFile(file))
  const hasInvalidSize = files.some(file => file.size > MAX_PHOTO_BYTES)
  if (hasInvalidType || hasInvalidSize) {
    const message =
      hasInvalidType && hasInvalidSize
        ? "Use JPEG, PNG, WEBP, or MP4 files, each 5MB or less."
        : hasInvalidType
          ? "Use JPEG, PNG, WEBP, or MP4 files."
          : "Each file must be 5MB or less."
    toast.error(message, {
      ...TOAST_OPTIONS,
      toastId: "city-hub-photo-file-validation",
    })
  }

  const valid = files.filter(
    file => isAcceptedPhotoFile(file) && file.size <= MAX_PHOTO_BYTES,
  )
  if (valid.length === 0) return

  const currentPreviews = Array.isArray(values?.previews) ? values.previews : []
  const removedMediaIds = [
    ...(Array.isArray(values?.removedMediaIds) ? values.removedMediaIds : []),
  ]

  // Replace one existing/new slot — keep every other preview.
  if (replacePreviewId) {
    const targetIndex = currentPreviews.findIndex(
      item => String(item?.id) === String(replacePreviewId),
    )
    if (targetIndex < 0) return

    const target = currentPreviews[targetIndex]
    if (target?.isExisting && target?.mediaId) {
      removedMediaIds.push(target.mediaId)
    }
    revokePhotoPreview(target)

    const replacement = mapFileToPhotoPreview(valid[0])
    const nextPreviews = [...currentPreviews]
    nextPreviews.splice(targetIndex, 1, replacement)

    const freeSlots = Math.max(0, MAX_PHOTO_FILES - nextPreviews.length)
    const extras = valid.slice(1, 1 + freeSlots).map(mapFileToPhotoPreview)

    setValues({
      ...values,
      previews: [...nextPreviews, ...extras],
      removedMediaIds: [...new Set(removedMediaIds.filter(Boolean))],
    })
    return
  }

  const remaining = Math.max(0, MAX_PHOTO_FILES - currentPreviews.length)
  if (remaining === 0) {
    toast.error(`You can upload up to ${MAX_PHOTO_FILES} files.`, {
      ...TOAST_OPTIONS,
      toastId: "city-hub-photo-file-limit",
    })
    return
  }

  if (valid.length > remaining) {
    toast.error(`You can upload up to ${MAX_PHOTO_FILES} files.`, {
      ...TOAST_OPTIONS,
      toastId: "city-hub-photo-file-limit",
    })
  }

  setValues({
    ...values,
    previews: [
      ...currentPreviews,
      ...valid.slice(0, remaining).map(mapFileToPhotoPreview),
    ],
  })
}

const AddPhotoModal = ({
  isOpen,
  onClose,
  cityId,
  photographyId = null,
  onSuccess,
}) => {
  const fileInputRef = useRef(null)
  const replaceTargetIdRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const formValuesRef = useRef(null)
  const { categories } = useCategories()
  const isEdit = Boolean(photographyId)
  const [initialValues, setInitialValues] = useState(getEmptyPhotographyForm)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [formKey, setFormKey] = useState(0)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!isOpen) {
      setInitialValues(getEmptyPhotographyForm())
      setIsLoadingDetail(false)
      return undefined
    }

    if (!photographyId) {
      setInitialValues({
        ...getEmptyPhotographyForm(),
        city_id: cityId || "",
      })
      setIsLoadingDetail(false)
      setFormKey(key => key + 1)
      return undefined
    }

    let cancelled = false
    setIsLoadingDetail(true)

    ;(async () => {
      const result = await fetchPhotographyDetail(photographyId)
      if (cancelled) return
      if (!result.ok) {
        setIsLoadingDetail(false)
        onCloseRef.current?.()
        return
      }
      setInitialValues(mapPhotographyToForm(result.item))
      setIsLoadingDetail(false)
      // Remount Formik once with loaded media — avoid enableReinitialize mid-edit.
      setFormKey(key => key + 1)
    })()

    return () => {
      cancelled = true
    }
  }, [isOpen, photographyId, cityId])

  const handleSubmit = async (values, { setSubmitting }) => {
    const resolvedCityId = values.city_id || cityId
    if (!resolvedCityId) {
      setSubmitting(false)
      return
    }

    try {
      const formData = buildPhotographyFormData({
        cityId: resolvedCityId,
        values: { ...values, city_id: resolvedCityId },
        isEdit,
      })
      // Edit uses POST + `_method=PATCH` so multipart `remove_media_ids[]` is parsed.
      const response = isEdit
        ? await post(endpoints.updatePhotography(photographyId), formData, {
            skipErrorToast: true,
          })
        : await post(endpoints.createPhotography, formData, {
            skipErrorToast: true,
          })

      if (!response?.data?.success) {
        toastApiError(
          response,
          isEdit ? "Failed to update photography" : "Failed to upload photography",
        )
        return
      }

      toast.success(
        response.data.message ||
          (isEdit
            ? "Photography updated successfully"
            : "Photography uploaded successfully"),
        TOAST_OPTIONS,
      )
      revokePhotoPreviews(values.previews)
      onSuccess?.()
      onClose?.()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      key={
        isOpen
          ? `photo-form-${photographyId || "create"}-${formKey}`
          : "closed"
      }
      initialValues={initialValues}
      enableReinitialize={false}
      validationSchema={photographySchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        setFieldTouched,
        setValues,
        isSubmitting,
      }) => {
        formValuesRef.current = values

        const closeModal = () => {
          if (isSubmitting) return
          revokePhotoPreviews(values.previews)
          onClose?.()
        }

        const removePreview = item => {
          revokePhotoPreview(item)
          setValues({
            ...values,
            previews: (values.previews || []).filter(
              preview => preview.id !== item.id,
            ),
            removedMediaIds:
              item.isExisting && item.mediaId
                ? [
                    ...new Set([
                      ...(values.removedMediaIds || []),
                      item.mediaId,
                    ]),
                  ]
                : values.removedMediaIds || [],
          })
        }

        const clearPreviews = () => {
          const removedIds = (values.previews || [])
            .filter(item => item.isExisting && item.mediaId)
            .map(item => item.mediaId)
          revokePhotoPreviews(values.previews)
          setValues({
            ...values,
            previews: [],
            removedMediaIds: [
              ...new Set([...(values.removedMediaIds || []), ...removedIds]),
            ],
          })
        }

        const openFilePicker = ({ replacePreviewId = null } = {}) => {
          replaceTargetIdRef.current = replacePreviewId
          fileInputRef.current?.click()
        }

        const onFileInputChange = event => {
          const replacePreviewId = replaceTargetIdRef.current
          replaceTargetIdRef.current = null
          acceptPhotoFiles(
            event.target.files,
            formValuesRef.current || values,
            setValues,
            { replacePreviewId },
          )
          event.target.value = ""
        }

        return (
          <Modal
            isOpen={isOpen}
            toggle={closeModal}
            centered
            backdrop={isSubmitting ? "static" : true}
            keyboard={!isSubmitting}
            className="amok-modal amok-modal--city-photo"
            contentClassName="amok-modal__content"
            backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
          >
            <ModalBody className="amok-modal__body city-hub-photo-modal__body">
              <Form className="city-hub-photo-modal" noValidate>
                <div className="city-hub-photo-modal__main">
                  <div className="city-hub-photo-modal__header">
                    <h3 className="city-hub-photo-modal__title">
                      {isEdit ? "Edit Media" : "Upload Media"}
                    </h3>
                    <p className="city-hub-photo-modal__help">
                      {isEdit
                        ? "Add more images or videos, remove any you don’t want, or use Change to replace one — same as editing tags."
                        : "Upload photos and videos from your event or concert shoot. Select up to 25 of your best shots."}
                    </p>
                  </div>

                  {isLoadingDetail ? (
                    <p className="city-hub-photo-modal__help">
                      Loading photography...
                    </p>
                  ) : (
                    <div className="city-hub-photo-modal__fields">
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="city-hub-photo-upload"
                        className="city-hub-photo-modal__upload-input"
                        accept={PHOTO_ACCEPT}
                        multiple={MAX_PHOTO_FILES > 1}
                        onChange={onFileInputChange}
                      />

                      {values.previews.length > 0 ? (
                        <div className="city-hub-photo-modal__preview-list">
                          <div className="city-hub-photo-modal__preview-grid">
                            {values.previews.map(item => (
                              <div
                                key={item.id}
                                className="city-hub-photo-modal__preview"
                              >
                                {isVideoMime(item.mimeType, item.url) ? (
                                  <video
                                    key={item.url || item.id}
                                    src={item.url || undefined}
                                    muted
                                    playsInline
                                    preload="metadata"
                                  />
                                ) : (
                                  <img
                                    key={item.url || item.id}
                                    src={item.url || ICONS.thumbPlaceholder}
                                    alt={item.name || ""}
                                    onError={event => {
                                      if (
                                        event.currentTarget.src !==
                                        ICONS.thumbPlaceholder
                                      ) {
                                        event.currentTarget.src =
                                          ICONS.thumbPlaceholder
                                      }
                                    }}
                                  />
                                )}
                                <div className="city-hub-photo-modal__preview-actions-overlay">
                                  <button
                                    type="button"
                                    className="city-hub-photo-modal__preview-replace"
                                    onClick={() =>
                                      openFilePicker({
                                        replacePreviewId: item.id,
                                      })
                                    }
                                    aria-label={`Replace ${item.name || "photo"}`}
                                  >
                                    Change
                                  </button>
                                  <button
                                    type="button"
                                    className="city-hub-photo-modal__preview-remove"
                                    onClick={() => removePreview(item)}
                                    aria-label={`Remove ${item.name || "photo"}`}
                                  >
                                    <img
                                      src={ICONS.callRemoveX}
                                      alt=""
                                      width={10}
                                      height={10}
                                    />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="city-hub-photo-modal__preview-actions">
                            <CustomButton
                              variant="secondary"
                              size="sm"
                              type="button"
                              onClick={() => openFilePicker()}
                              disabled={
                                values.previews.length >= MAX_PHOTO_FILES
                              }
                            >
                              Add more
                            </CustomButton>
                            <CustomButton
                              variant="danger"
                              size="sm"
                              type="button"
                              onClick={clearPreviews}
                            >
                              Clear all
                            </CustomButton>
                          </div>
                          {touched.previews && errors.previews ? (
                            <p className="amok-field__message amok-field__message--error">
                              {errors.previews}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <div
                          className="city-hub-photo-modal__upload"
                          role="button"
                          tabIndex={0}
                          onClick={() => openFilePicker()}
                          onKeyDown={event => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault()
                              openFilePicker()
                            }
                          }}
                          onDragOver={event => {
                            event.preventDefault()
                            event.stopPropagation()
                          }}
                          onDrop={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            replaceTargetIdRef.current = null
                            acceptPhotoFiles(
                              event.dataTransfer?.files,
                              values,
                              setValues,
                            )
                          }}
                        >
                          <span
                            className="city-hub-photo-modal__upload-icon"
                            aria-hidden="true"
                          >
                            <img
                              src={ICONS.callUploadExport}
                              alt=""
                              width={16}
                              height={16}
                            />
                          </span>
                          <p className="city-hub-photo-modal__upload-title">
                            Click to upload or drag and drop multiple files
                          </p>
                          <p className="city-hub-photo-modal__upload-hint">
                            JPEG, PNG, WEBP, or MP4 (up to 25 files, max 5MB
                            each)
                          </p>
                          {touched.previews && errors.previews ? (
                            <p className="amok-field__message amok-field__message--error">
                              {errors.previews}
                            </p>
                          ) : null}
                        </div>
                      )}

                      <CustomInput
                        label="Event / Album Title"
                        name="title"
                        required
                        maxLength={PHOTO_TITLE_MAX}
                        placeholder="e.g: Summer Music Festival 2026"
                        value={values.title}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("title", true)}
                        error={touched.title && errors.title}
                        helperText={`${values.title.length}/${PHOTO_TITLE_MAX}`}
                      />

                      <CustomInput
                        label="Description"
                        name="description"
                        textarea
                        rows={3}
                        required
                        maxLength={PHOTO_DESCRIPTION_MAX}
                        placeholder="Add a brief description of the event or shoot..."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={() => setFieldTouched("description", true)}
                        error={touched.description && errors.description}
                        helperText={`${values.description.length}/${PHOTO_DESCRIPTION_MAX}`}
                      />

                      <CategoriesMultiselect
                        className="city-hub-photo-modal__tags-field"
                        label="Tags"
                        name="tags"
                        required
                        options={categories}
                        selected={values.tags}
                        checkIconSrc={ICONS.check}
                        onChange={next => {
                          setFieldValue("tags", next)
                          setFieldTouched("tags", true, false)
                        }}
                        error={touched.tags && errors.tags}
                      />
                    </div>
                  )}
                </div>

                <div className="city-hub-photo-modal__actions">
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    className="city-hub-photo-modal__cancel"
                    onClick={closeModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="primary"
                    size="md"
                    type="submit"
                    className="city-hub-photo-modal__submit"
                    loading={isSubmitting}
                    disabled={isLoadingDetail}
                  >
                    {isEdit ? "Save Changes" : "Upload Media"}
                  </CustomButton>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        )
      }}
    </Formik>
  )
}

const EXPLORE_OTHER_CITIES_SKELETON_COUNT = 4
const EXPLORE_LOAD_MORE_SKELETON_COUNT = 2
const EXPLORE_CITIES_PAGE_SIZE = 10

const ExploreCityRowSkeleton = () => (
  <div
    className="city-hub-explore-modal__row city-hub-explore-modal__row--skeleton"
    aria-hidden="true"
  >
    <div className="amok-skeleton city-hub-explore-modal__thumb-skel" />
    <div className="city-hub-explore-modal__info">
      <div className="amok-skeleton city-hub-explore-modal__line-skel city-hub-explore-modal__line-skel--name" />
      <div className="amok-skeleton city-hub-explore-modal__line-skel city-hub-explore-modal__line-skel--meta" />
    </div>
    <div className="amok-skeleton city-hub-explore-modal__action-skel" />
  </div>
)

const mergeExploreCityPages = (prev, next) => {
  const seen = new Set(prev.map(item => String(item.id)))
  return [
    ...prev,
    ...next.filter(item => item?.id && !seen.has(String(item.id))),
  ]
}

const ExploreCitiesModal = ({
  isOpen,
  onClose,
  activeCityId = null,
  yourCities = [],
  userLevel = 1,
  onSelectCity,
  onSwitchCity,
  onVisitCity,
  isPublic = false,
}) => {
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [otherCities, setOtherCities] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingCities, setIsLoadingCities] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [activeId, setActiveId] = useState(activeCityId)
  const latestRequest = useRef(0)
  const pageRef = useRef(1)
  const loadInFlightRef = useRef(false)
  const sentinelRef = useRef(null)
  const listScrollRootRef = useRef(null)
  const safeUserLevel = Math.max(1, Number(userLevel) || 1)

  useEffect(() => {
    if (isOpen) {
      setActiveId(activeCityId)
      setSearch("")
      setDebouncedSearch("")
      setOtherCities([])
      setHasMore(false)
      pageRef.current = 1
    }
  }, [isOpen, activeCityId])

  const debouncedSetSearch = useMemo(
    () =>
      debounce(value => {
        setDebouncedSearch(value)
      }, 300),
    [],
  )

  useEffect(
    () => () => {
      debouncedSetSearch.cancel()
    },
    [debouncedSetSearch],
  )

  const ownedCityIds = useMemo(
    () =>
      new Set(
        (yourCities || [])
          .map(city => String(city?.id || ""))
          .filter(Boolean),
      ),
    [yourCities],
  )

  const fetchOtherCities = useCallback(
    async ({ page: pageToLoad = 1, append = false, query = "" } = {}) => {
      const requestId = ++latestRequest.current
      if (append) setIsLoadingMore(true)
      else setIsLoadingCities(true)

      try {
        const response = await get(endpoints.getCites, {
          params: {
            search: query || "",
            page: pageToLoad,
            page_size: EXPLORE_CITIES_PAGE_SIZE,
            per_page: EXPLORE_CITIES_PAGE_SIZE,
            sort_direction: "asc",
          },
        })

        if (requestId !== latestRequest.current) return

        if (!response?.data?.success) {
          toastApiError(response, "Failed to load cities")
          if (!append) setOtherCities([])
          setHasMore(false)
          return
        }

        const { list, currentPage, hasMore: nextHasMore } =
          unwrapCityHubPagedResult(response, {
            pageToLoad,
            pageSize: EXPLORE_CITIES_PAGE_SIZE,
          })

        const mapped = list
          .map(city => mapApiCityToCityHubCity(city))
          .filter(city => city.id && !ownedCityIds.has(String(city.id)))

        setOtherCities(prev =>
          append ? mergeExploreCityPages(prev, mapped) : mapped,
        )
        pageRef.current = currentPage
        setHasMore(nextHasMore)
      } catch (error) {
        if (requestId !== latestRequest.current) return
        toastApiError(error, "Failed to load cities")
        if (!append) setOtherCities([])
        setHasMore(false)
      } finally {
        if (requestId === latestRequest.current) {
          loadInFlightRef.current = false
          setIsLoadingCities(false)
          setIsLoadingMore(false)
        }
      }
    },
    [ownedCityIds],
  )

  useEffect(() => {
    if (!isOpen) return undefined
    pageRef.current = 1
    loadInFlightRef.current = false
    fetchOtherCities({
      page: 1,
      append: false,
      query: debouncedSearch.trim(),
    })
    return undefined
  }, [isOpen, debouncedSearch, fetchOtherCities])

  const loadMore = useCallback(() => {
    if (
      !hasMore ||
      isLoadingCities ||
      isLoadingMore ||
      loadInFlightRef.current
    ) {
      return
    }
    loadInFlightRef.current = true
    fetchOtherCities({
      page: pageRef.current + 1,
      append: true,
      query: debouncedSearch.trim(),
    })
  }, [
    debouncedSearch,
    fetchOtherCities,
    hasMore,
    isLoadingCities,
    isLoadingMore,
  ])

  useInfinitePagination({
    hasMore: hasMore && !isLoadingCities,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    resetKey: otherCities.length,
    sentinelRef,
    rootRef: listScrollRootRef,
    enabled: isOpen,
  })

  const handleSearchChange = event => {
    const value = event.target.value
    setSearch(value)
    debouncedSetSearch(value.trim())
  }

  const handleSelect = city => {
    if (!city?.id) return

    const nextCity = isPublic
      ? city
      : resolveCityHubCityForLevel(city, safeUserLevel)

    setActiveId(city.id)
    onSelectCity?.(nextCity)
    onSwitchCity?.(nextCity)
    onVisitCity?.(nextCity)
    onClose?.()
  }

  const showInitialSkeleton = isLoadingCities
  const showLoadMoreSkeleton = !showInitialSkeleton && isLoadingMore

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--city-explore"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body city-hub-explore-modal__body">
        <div className="city-hub-explore-modal" ref={listScrollRootRef}>
          <div className="city-hub-explore-modal__main">
            <div className="city-hub-explore-modal__header">
              <h3 className="city-hub-explore-modal__title">Explore Cities</h3>
              {isPublic ? (
                <p className="city-hub-explore-modal__help">
                  Browse arts communities around the world. Request an invite to
                  join a city and start collaborating.
                </p>
              ) : (
                <>
                  <p className="city-hub-explore-modal__help">
                    Switch into cities you&apos;ve fully unlocked, or visit the
                    rest to browse. Full access: Primary at Level 1, Secondary
                    at Level 2, Tertiary at Level 3.
                  </p>
                  <p
                    className="city-hub-explore-modal__level"
                    aria-live="polite"
                  >
                    Your level:{" "}
                    <span className="city-hub-explore-modal__level-value">
                      Level {safeUserLevel}
                    </span>
                  </p>
                </>
              )}
            </div>

            {!isPublic ? (
              <>
                <div className="city-hub-explore-modal__section">
                  <div className="city-hub-explore-modal__section-head">
                    <span className="city-hub-explore-modal__section-label">
                      Your Cities
                    </span>
                    <div
                      className="city-hub-explore-modal__divider"
                      aria-hidden="true"
                    />
                  </div>

                  <div className="city-hub-explore-modal__list">
                    {yourCities.length === 0 ? (
                      <div className="city-hub-explore-modal__empty">
                        No cities on your profile yet.
                      </div>
                    ) : (
                      yourCities.map(city => {
                        const isActive = String(city.id) === String(activeId)
                        const isVisitOnly = !isOwnedCityHubAccessible(
                          city,
                          safeUserLevel,
                        )
                        const requiredLevel =
                          getCityTierRequiredLevel(
                            city.addressType || city.tier,
                          ) || 1
                        const visitHint = `Visit until Level ${requiredLevel}`

                        return (
                          <div
                            key={`${city.tier || "city"}-${city.id}`}
                            className="city-hub-explore-modal__row"
                          >
                            {city.thumb ? (
                              <img
                                src={city.thumb}
                                alt=""
                                className="city-hub-explore-modal__thumb"
                                width={36}
                                height={36}
                              />
                            ) : (
                              <div
                                className="city-hub-explore-modal__thumb city-hub-explore-modal__thumb--placeholder"
                                aria-hidden="true"
                              />
                            )}
                            <div className="city-hub-explore-modal__info">
                              <div className="city-hub-explore-modal__name-row">
                                <span className="city-hub-explore-modal__name">
                                  {city.name}
                                </span>
                                {city.tierLabel ? (
                                  <span
                                    className={`city-hub-explore-modal__tier city-hub-explore-modal__tier--${city.tier}`}
                                  >
                                    {city.tierLabel}
                                  </span>
                                ) : null}
                              </div>
                              {isVisitOnly ? (
                                <span className="city-hub-explore-modal__members">
                                  {visitHint}
                                  {city.subtitle ? ` · ${city.subtitle}` : ""}
                                </span>
                              ) : city.subtitle ? (
                                <span className="city-hub-explore-modal__members">
                                  {city.subtitle}
                                </span>
                              ) : null}
                            </div>
                            {isActive ? (
                              <span className="city-hub-explore-modal__active">
                                <span
                                  className="city-hub-explore-modal__active-dot"
                                  aria-hidden="true"
                                />
                                Active
                              </span>
                            ) : (
                              <CustomButton
                                variant="toned"
                                size="sm"
                                type="button"
                                className="city-hub-explore-modal__action"
                                trailingIconSrc={ICONS.arrowRight12}
                                trailingIconAlt=""
                                onClick={() => handleSelect(city)}
                              >
                                {isVisitOnly ? "Visit City" : "Switch"}
                              </CustomButton>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>

                <div
                  className="city-hub-explore-modal__divider city-hub-explore-modal__divider--section"
                  aria-hidden="true"
                />
              </>
            ) : null}

            <div className="city-hub-explore-modal__section">
              <span className="city-hub-explore-modal__section-label">
                {isPublic ? "Cities" : "Other Cities"}
              </span>
              <div className="city-hub-explore-modal__search">
                <img
                  src={ICONS.search}
                  alt=""
                  width={14}
                  height={14}
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search cities..."
                  aria-label="Search cities"
                />
              </div>

              <div
                className="city-hub-explore-modal__list"
                aria-busy={
                  showInitialSkeleton || showLoadMoreSkeleton || undefined
                }
              >
                {showInitialSkeleton
                  ? Array.from(
                      { length: EXPLORE_OTHER_CITIES_SKELETON_COUNT },
                      (_, index) => (
                        <ExploreCityRowSkeleton
                          key={`explore-city-skel-${index}`}
                        />
                      ),
                    )
                  : null}

                {!showInitialSkeleton &&
                  otherCities.map(city => {
                    const isActive = String(city.id) === String(activeId)
                    return (
                      <div
                        key={city.id}
                        className="city-hub-explore-modal__row"
                      >
                        {city.thumb ? (
                          <img
                            src={city.thumb}
                            alt=""
                            className="city-hub-explore-modal__thumb"
                            width={36}
                            height={36}
                          />
                        ) : (
                          <div
                            className="city-hub-explore-modal__thumb city-hub-explore-modal__thumb--placeholder"
                            aria-hidden="true"
                          />
                        )}
                        <div className="city-hub-explore-modal__info">
                          <span className="city-hub-explore-modal__name">
                            {city.name}
                          </span>
                          {city.subtitle ? (
                            <span className="city-hub-explore-modal__members">
                              {city.subtitle}
                            </span>
                          ) : null}
                        </div>
                        {isActive ? (
                          <span className="city-hub-explore-modal__active">
                            <span
                              className="city-hub-explore-modal__active-dot"
                              aria-hidden="true"
                            />
                            Active
                          </span>
                        ) : (
                          <CustomButton
                            variant="toned"
                            size="sm"
                            type="button"
                            className="city-hub-explore-modal__action"
                            trailingIconSrc={ICONS.arrowRight12}
                            trailingIconAlt=""
                            onClick={() => handleSelect(city)}
                          >
                            {isPublic ? "View City" : "Visit City"}
                          </CustomButton>
                        )}
                      </div>
                    )
                  })}

                {showLoadMoreSkeleton
                  ? Array.from(
                      { length: EXPLORE_LOAD_MORE_SKELETON_COUNT },
                      (_, index) => (
                        <ExploreCityRowSkeleton
                          key={`explore-city-more-skel-${index}`}
                        />
                      ),
                    )
                  : null}

                {!showInitialSkeleton &&
                !showLoadMoreSkeleton &&
                otherCities.length === 0 ? (
                  <div className="city-hub-explore-modal__empty">
                    No cities match your search.
                  </div>
                ) : null}

                <InfiniteListSentinel
                  sentinelRef={sentinelRef}
                  hasMore={hasMore && !isLoadingCities}
                  className="city-hub-explore-modal__infinite-sentinel"
                />
              </div>
            </div>
          </div>

          <div className="city-hub-explore-modal__actions">
            <CustomButton
              variant="danger"
              size="md"
              type="button"
              className="city-hub-explore-modal__cancel"
              onClick={onClose}
            >
              Cancel
            </CustomButton>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

const PhotographyPreviewModal = ({
  isOpen,
  onClose,
  photographyId = null,
  itemId = null,
  fetchDetail = fetchPhotographyDetail,
  mapMedias = mapPhotographyDetailMedias,
  previewLabel = "Photography",
  emptyDescription = "This photography item does not include any images yet.",
}) => {
  const resolvedId = itemId || photographyId
  const [isLoading, setIsLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const onCloseRef = useRef(onClose)
  const fetchDetailRef = useRef(fetchDetail)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    fetchDetailRef.current = fetchDetail
  }, [fetchDetail])

  const medias = useMemo(
    () => mapMedias(detail),
    [detail, mapMedias],
  )
  const tags = useMemo(() => mapPhotographyDetailTags(detail), [detail])
  const safeIndex =
    medias.length === 0
      ? 0
      : Math.min(Math.max(activeIndex, 0), medias.length - 1)
  const current = medias[safeIndex] || null
  const hasPrev = safeIndex > 0
  const hasNext = safeIndex < medias.length - 1

  useEffect(() => {
    if (activeIndex !== safeIndex) setActiveIndex(safeIndex)
  }, [activeIndex, safeIndex])

  useEffect(() => {
    if (!isOpen) {
      setDetail(null)
      setActiveIndex(0)
      setIsLoading(false)
      return undefined
    }

    if (!resolvedId) {
      setDetail(null)
      setIsLoading(false)
      return undefined
    }

    const controller = new AbortController()
    setIsLoading(true)
    setActiveIndex(0)

    ;(async () => {
      const loadDetail = fetchDetailRef.current || fetchPhotographyDetail
      const result = await loadDetail(resolvedId, {
        signal: controller.signal,
      })
      if (controller.signal.aborted || result?.aborted) return
      if (!result.ok) {
        setDetail(null)
        setIsLoading(false)
        onCloseRef.current?.()
        return
      }
      setDetail(result.item)
      setIsLoading(false)
    })()

    return () => {
      controller.abort()
    }
  }, [isOpen, resolvedId])

  useEffect(() => {
    if (!isOpen || medias.length <= 1) return undefined

    const handleKeyDown = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        setActiveIndex(index => Math.max(0, index - 1))
      }
      if (event.key === "ArrowRight") {
        event.preventDefault()
        setActiveIndex(index => Math.min(medias.length - 1, index + 1))
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, medias.length])

  const goPrev = event => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    setActiveIndex(index => Math.max(0, index - 1))
  }

  const goNext = event => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    setActiveIndex(index => Math.min(medias.length - 1, index + 1))
  }

  const title = String(detail?.title || "").trim() || previewLabel
  const description = String(detail?.description || "").trim()
  const createdLabel = formatDate(detail?.created_at, {
    format: "MMM D, YYYY",
    fallback: "",
  })
  const uploader = detail?.uploader || detail?.user
  const photographerName =
    uploader?.full_name ||
    [uploader?.first_name, uploader?.last_name].filter(Boolean).join(" ").trim() ||
    ""
  const photographerAvatar = getUserAvatarUrl(uploader)

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="xl"
      className="amok-modal amok-modal--photo-preview"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
      scrollable
    >
      <CustomIconButton
        iconSrc={ICONS.callRemoveX}
        label="Close preview"
        size="md"
        className="photo-preview-modal__close-btn"
        onClick={onClose}
      />
      <ModalBody className="amok-modal__body photo-preview-modal__body">
        <div className="photo-preview-modal">
          <header className="photo-preview-modal__header">
            <div className="photo-preview-modal__heading">
              <div className="photo-preview-modal__title-row">
                <h2 className="photo-preview-modal__title">{title}</h2>
                {createdLabel ? (
                  <span className="photo-preview-modal__date">{createdLabel}</span>
                ) : null}
              </div>
              {description ? (
                <p className="photo-preview-modal__description">{description}</p>
              ) : null}
              {tags.length > 0 ? (
                <div className="photo-preview-modal__tags">
                  {tags.map(tag => (
                    <span key={tag.id} className="photo-preview-modal__tag">
                      <span
                        className="photo-preview-modal__tag-dot"
                        aria-hidden="true"
                      />
                      {tag.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </header>

          <div
            className="photo-preview-modal__stage-wrap"
            aria-busy={isLoading || undefined}
          >
            {isLoading ? (
              <div className="photo-preview-modal__loading" aria-hidden="true">
                <span className="amok-skeleton photo-preview-modal__skel" />
              </div>
            ) : current ? (
              <>
                <button
                  type="button"
                  className="photo-preview-modal__nav photo-preview-modal__nav--prev"
                  aria-label="Previous media"
                  disabled={!hasPrev}
                  onClick={goPrev}
                >
                  <img src={ICONS.chevronLeft} alt="" width={16} height={16} />
                </button>

                <button
                  type="button"
                  className="photo-preview-modal__nav photo-preview-modal__nav--next"
                  aria-label="Next media"
                  disabled={!hasNext}
                  onClick={goNext}
                >
                  <img
                    src={ICONS.chevronRight}
                    alt=""
                    width={16}
                    height={16}
                  />
                </button>

                <div className="photo-preview-modal__stage">
                  <div className="photo-preview-modal__media-shell">
                    {current.isVideo ? (
                      <video
                        key={`video-${current.id}-${safeIndex}`}
                        className="photo-preview-modal__media"
                        src={current.url}
                        controls
                        autoPlay
                        playsInline
                      />
                    ) : (
                      <MediaFallbackImage
                        key={`image-${current.id}-${safeIndex}`}
                        className="photo-preview-modal__media"
                        src={current.url}
                        fallback={ICONS.thumbnailPlaceholder}
                        alt={title}
                        draggable={false}
                      />
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="photo-preview-modal__empty">
                <EmptyState
                  size="sm"
                  title="No media found"
                  description={emptyDescription}
                />
              </div>
            )}
          </div>

          <footer className="photo-preview-modal__footer">
            <div className="photo-preview-modal__footer-top">
              {photographerName ? (
                <div className="photo-preview-modal__photographer">
                  <Avatar
                    src={photographerAvatar}
                    name={photographerName}
                    size="xs"
                  />
                  <span>{photographerName}</span>
                </div>
              ) : (
                <span />
              )}
              {!isLoading && medias.length > 0 ? (
                <span className="photo-preview-modal__counter">
                  {safeIndex + 1} / {medias.length}
                </span>
              ) : null}
            </div>

            {!isLoading && medias.length > 1 ? (
              <div
                className="photo-preview-modal__thumbs"
                role="tablist"
                aria-label="Photography media"
              >
                {medias.map((media, index) => (
                  <button
                    key={media.id || `thumb-${index}`}
                    type="button"
                    role="tab"
                    aria-selected={index === safeIndex}
                    className={`photo-preview-modal__thumb${
                      index === safeIndex
                        ? " photo-preview-modal__thumb--active"
                        : ""
                    }`}
                    onClick={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      setActiveIndex(index)
                    }}
                    aria-label={`Show media ${index + 1}`}
                  >
                    {media.isVideo ? (
                      <video
                        src={media.url}
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <MediaFallbackImage
                        src={media.url}
                        fallback={ICONS.thumbPlaceholder}
                        alt=""
                      />
                    )}
                  </button>
                ))}
              </div>
            ) : null}
          </footer>
        </div>
      </ModalBody>
    </Modal>
  )
}


export {
  AddReviewModal,
  BecomeReviewWriterModal,
  BecomeContributorModal,
  AddPhotoModal,
  ExploreCitiesModal,
  PhotographyPreviewModal,
}
